<template>
    <span class="status-badge">
        <span
              :class="badgeClasses"
              :title="title">
            <b-icon
                    :icon="icon"
                    class="status-icon">
            </b-icon>
            <slot>
                <span v-if="showText" class="status-text">{{ text }}</span>
            </slot>
        </span>
    </span>
</template>

<script>
export default {
    name: 'StatusBadge',
    props: {
        icon: {
            type: String,
            required: true
        },

        variant: {
            type: String,
            default: 'info',
            validator: value => ['success', 'warning', 'danger', 'info'].includes(value)
        },
        text: {
            type: String,
            default: ''
        },
        showText: {
            type: Boolean,
            default: true
        },
        title: {
            type: String,
            default: ''
        }
    },
    computed: {
        badgeClasses() {
            return {
                'status-badge-inner': true,
                [`status-${this.variant}`]: true
            }
        }
    }
}
</script>

<style lang="scss" scoped>
$success-bg-light: #fff;
$success-border: #5cb85c;
$success-text: $body-color;

$warning-bg-light: #fff8dc;
$warning-border: #f0ad4e;
$warning-text: #856404;

$danger-bg-light: #fdf2f2;
$danger-border: #d9534f;
$danger-text: #721c24;

$info-bg-light: #e8f7fa;
$info-border: #5bc0de;
$info-text: #0c5460;

.status-badge {
    display: block;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
}

.status-badge-inner {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem;
}

.status-icon {
    font-size: 0.875rem;
}

.status-text {
    font-weight: 600;
}

.status-success {
    background: $success-bg-light;
    border: 1px solid $success-border;
    color: $success-text;

    .status-icon {
        color: $success-border;
    }
}

.status-warning {
    background: $warning-bg-light;
    border: 1px solid $warning-border;
    color: $warning-text;

    .status-icon {
        color: $warning-border;
    }
}

.status-danger {
    background: $danger-bg-light;
    border: 1px solid $danger-border;
    color: $danger-text;

    .status-icon {
        color: $danger-border;
    }
}

.status-info {
    background: $info-bg-light;
    border: 1px solid $info-border;
    color: $info-text;

    .status-icon {
        color: $info-border;
    }
}

// For connected layout, wrap multiple status badges in a container with display: flex
.status-group {
    display: flex;
    flex-direction: column;
    gap: 0;

    .status-badge {
        margin-right: 0;
        margin-bottom: 0;
        flex: 1;
        min-width: 0;

        .status-badge-inner {
            width: 100%;
            justify-content: flex-start;
            border-radius: 0;
        }

        &:not(:first-child):not(:last-child) .status-badge-inner {
            border-top: none;
        }

        &:first-child:not(:last-child) .status-badge-inner {
            border-top-left-radius: 0.5rem;
            border-top-right-radius: 0.5rem;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &:last-child:not(:first-child) .status-badge-inner {
            border-bottom-left-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            border-top: none;
        }

        &:only-child .status-badge-inner {
            border-radius: 0.5rem;
        }
    }
}
</style>
